import { debounce } from 'lodash'
import { observable, computed, action, toJS } from 'mobx'
import { apis, request } from '@/utils/net'
import util from '@/utils/util'

const DISCOUNT_TYPE_KEY = 'discount'
// 默认的发票数据
const defaultInvoiceData = {
    client_info: {}, // 开票方（商户）信息
    buyer_info: {}, // 购方信息
    goods_info: [], // 商品（开票项目）信息
    invoice_info: {}, // 发票信息
}
// 默认的开票项目数据
const defaultInvoiceProject = {
    name: '', // 货物或应税劳务、服务名称
    models: '', // 规格型号
    unit: '', // 单位
    total: '', // 数量
    price: '', // 单价（不含税）
    price_with_tax: '', // 单价（含税）
    total_price: '', // 总金额（不含税）
    total_price_with_tax: '', // 总金额（含税）
    tax_rate: '0', // 税率
    tax_amount: '', // 税额
}
// 默认的开票汇总数据
const defaultInvoiceCountInfo = {
    total_tax: '0.00', // 总税额
    total_price: '0.00', // 总金额（不含税）
    total_price_with_tax: '0.00', // 总金额（价税合计）
    total_rmb: '零', // 总金额（价税合计，大写）
}

const uuid = () => util.uuid(16)
const assignKey = (...args) => Object.assign({}, ...args, {_key: uuid()})

class Store {
    // 发票数据
    @observable.shallow invoiceData = {...defaultInvoiceData}
    // 发票抬头类型，1个人，2企业
    @computed get invoiceTitleType() {
        return this.invoiceData.buyer_info.title_type
    }
    @computed get clientInfo() {
        return this.invoiceData.client_info
    }
    @computed get buyerInfo() {
        return this.invoiceData.buyer_info
    }
    @computed get invoiceProjects() {
        return this.invoiceData.goods_info
    }
    @computed get invoiceInfo() {
        return this.invoiceData.invoice_info
    }

    // 保存开票项数据
    @observable recordList = [{}]
    @action recordListChange(rowIndex) {
        this.recordList[rowIndex] = this.invoiceData.goods_info[rowIndex]
    }

    // 税率列表
    @observable.shallow taxList = []
    // 由于observable后是一个对象，所以这里用计算属性来取值，避免组件用到了prop-types校验而报warning
    @computed get taxRateList() {
        return toJS(this.taxList) || []
    }

    //获取税率列表
    @action fetchTaxList(){
        return request({url: apis.getTaxRate}).then(res => {
            return this.taxList = (res || []).filter(item => item.is_deleted !== '1')
        })
    }

    @action initInvoiceData(invoiceData) {
        let invoiceProjects = (invoiceData.goods_info || []).slice(0)
        // 处理存在折扣信息的开票项目
        if (invoiceProjects && invoiceProjects.length) {
            for (let i = 0; i < invoiceProjects.length; i++) {
                const project = invoiceProjects[i]
                // 给数据添加唯一标识key值
                project._key = uuid()

                // 判断是否存在折扣信息，如果存在，向数组添加折扣行数据
                // 这里需要判断 discount 的值是否大于0，避免初始化数据为空串时，而使得页面出现了空的折扣行信息
                if (this._isDiscountProject(project) && Math.abs(project.discount) > 0) {
                    const discountData = this._newDiscount(project)
                    // 插入折扣行数据，这里直接把索引值i＋1，以便下次循环跳过该行
                    invoiceProjects.splice(++i, 0, discountData)
                }
            }
        }
        // 如果开票项目为空，添加默认开票行数据
        else {
            invoiceProjects = [this._newInvoiceProject()]
        }

        this.invoiceData = {
            ...defaultInvoiceData,
            ...invoiceData,
            // 开票项目
            goods_info: invoiceProjects,
            // 发票信息，写入默认的发票汇总信息
            invoice_info: {
                ...defaultInvoiceCountInfo,
                ...invoiceData.invoice_info
            }
        }
    }

    /**
     * 根据 key 值更新 this.invoiceData
     * @param  {String} dataKey  this.invoiceData对象中的key
     * @param  {Any} value       目标值
     */
    _updateInvoiceData(dataKey, value) {
        this.invoiceData = { ...this.invoiceData, [dataKey]: value }
        // 如果更新了开票项目数据，重新执行计算逻辑
        if (dataKey === 'goods_info') {
            this._countInvoice()
        }
    }

    @action setInvoiceTitleType(titleType) {
        const buyerInfo = this.buyerInfo
        buyerInfo.title_type = titleType
        this._updateInvoiceData('buyer_info', buyerInfo)
    }

    _newInvoiceProject() {
        return assignKey({ ...defaultInvoiceProject })
    }

    @action addInvoiceProject() {
        // 给默认行数据增加key值，form.getFieldDecorator()时用到
        const invoiceProjects = this.invoiceProjects.concat(this._newInvoiceProject())
        this._updateInvoiceProjects(invoiceProjects)
    }

    @action batchAddInvoiceProject(records) {
        if (!records || !records.length) {
            return false
        }
        // 添加的数据，以默认行数据defaultInvoiceProject为准，并添加key值
        let invoiceProjects = records.map(record => assignKey(defaultInvoiceProject, record))
        invoiceProjects = this.invoiceProjects.concat(invoiceProjects)
        this._updateInvoiceProjects(invoiceProjects)
    }

    /**
     * 判断开票项目是否存在折扣信息
     */
    _isDiscountProject(project) {
        return project.dis_type === 1 || project.dis_type === 2
    }

    /**
     * 判断记录是否为折扣行数据
     */
    _isDiscountRecord(record) {
        if (!record) return
        return record._type === DISCOUNT_TYPE_KEY
    }

    /**
     * 清除开票项目的折扣信息
     */
    _clearDiscountData(project) {
        return {
            ...project,
            _type: undefined,
            dis_type: undefined,
            dis_total_price: undefined,
            dis_tax: undefined,
            dis_value: undefined,
            discount: undefined
        }
    }

    _deleteInvoiceProject(invoiceProjects, rowIndex) {
        const project = invoiceProjects[rowIndex]
        let deleteCount = 0

        // 如果同时选择了开票项目及其折扣行，可能上一次操作已同时删除了它的折扣行数据，
        if (!project) {
            return deleteCount
        }

        // 删除折扣行数据需要清除其关联的开票项目的折扣信息
        if (this._isDiscountRecord(project)) {
            invoiceProjects[rowIndex - 1] = this._clearDiscountData(invoiceProjects[rowIndex - 1])
            invoiceProjects.splice(rowIndex, 1)
            deleteCount = 1
        }
        // 开票项目，判断它是否存在折扣信息，如果存在，连同一起删除它的折扣行数据
        else {
            if (this._isDiscountProject(project) && this._isDiscountRecord(invoiceProjects[rowIndex + 1])) {
                invoiceProjects.splice(rowIndex, 2)
                deleteCount = 2
            } else {
                invoiceProjects.splice(rowIndex, 1)
                deleteCount = 1
            }
        }

        return deleteCount
    }

    @action deleteInvoiceProject(rowIndex) {
        const invoiceProjects = this.invoiceProjects.slice(0)
        this._deleteInvoiceProject(invoiceProjects, rowIndex)
        this._updateInvoiceProjects(invoiceProjects)
    }

    @action batchDeleteInvoiceProject(rowIndex) {
        if (!rowIndex || !rowIndex.length) {
            return false
        }

        // 这里需要对传递过来的rowIndex进行升序排序，否则在删除数组元素时可能会报错
        rowIndex.sort((a, b) => a - b)
        // 累计当前已删除的数据量
        let deleteCount = 0
        const invoiceProjects = this.invoiceProjects.slice(0)

        for (let i = 0; i < rowIndex.length; i++) {
            const index = rowIndex[i] - deleteCount
            deleteCount += this._deleteInvoiceProject(invoiceProjects, index)
        }

        this._updateInvoiceProjects(invoiceProjects)
    }

    _updateInvoiceProjects(invoiceProjects) {
        this._updateInvoiceData('goods_info', invoiceProjects)
    }

    /**
     * 根据选择的商品信息来修改开票项目数据
     * @param  {Array}  goods        选中的商品信息，支持多个
     * @param  {Number} rowIndex     要修改的开票项目数据的行索引
     */
    @action updateInvoiceProject(goods, rowIndex) {
        const invoiceProjects = this.invoiceProjects
        const project = invoiceProjects[rowIndex]
        const nextProject = invoiceProjects[rowIndex + 1]

        // 如果存在折扣行数据即删除
        if (this._isDiscountProject(project) && this._isDiscountRecord(nextProject)) {
            invoiceProjects.splice(rowIndex + 1, 1)
        }

        // 更新当前开票项目
        // 这里用默认的开票项目数据和选中数据合并，但key值不能丢弃，
        // 否则form.getFieldDecorator()会生成新的field
        const newProject = {...defaultInvoiceProject, ...goods[0], _key: project._key}
        invoiceProjects.splice(rowIndex, 1, newProject)

        this._updateInvoiceProjects(invoiceProjects)

        // 如果选中的商品信息有多条，在修改当前开票行数据后，额外新增剩下的
        if (goods.length > 1) {
           this.batchAddInvoiceProject(goods.slice(1))
        }
    }

    _isEmptyField(project, field) {
        return typeof project[field] === 'undefined' || project[field] === ''
    }

    /**
     * 更新开票项目表单数据
     * @param  {String} fieldName       字段名
     * @param  {Any}    value           值
     * @param  {Number} rowIndex        开票数据的行索引，标识当前修改的是哪一条开票项目数据
     */
    @action changeInvoiceProjectFieldValue(fieldName, value, rowIndex) {
        const invoiceProjects = this.invoiceProjects.slice(0)
        const project = invoiceProjects[rowIndex]

        project[fieldName] = value

        if (this._isDiscountRecord(project)) {
            // 修改其关联的开票项目的dis_value，后面计算时用到
            const sourceProject = invoiceProjects[rowIndex - 1]
            sourceProject.dis_value = value
        } else {
            const fields = ['total', 'price_with_tax', 'total_price_with_tax']
            // 数量、单价、金额三个输入框，改了任意一个后，可能需要重新计算另两个的值
            if (fields.includes(fieldName)) {
                // 数量、单价、金额输入值小于0时，不处理，避免计算接口报错
                // 而数字输入框组件在失去焦点后，会自动赋以最小值0
                if (Number(value) < 0 || isNaN(value)) {
                    return false
                }

                const emptyLength = fields.filter(field => this._isEmptyField(project, field)).length
                // 非成品油计算逻辑
                // 都不为空时，输入数量或单价时，计算金额；当输入金额时，计算数量
                // 其它情况，这里不需要处理，有任意一个不为空的时候，按正常逻辑计算即可
                // 成品油计算逻辑
                // 总金额保持不变，当输入单价的时候改变数量，输入数量的时候改变单价
                // 当改变总金额的时候，保留数量的值，更改单价
                if (emptyLength === 0) {
                    // if (project.special_invoice_kind == '08'){
                    //     if (fieldName === 'price_with_tax') { // 单价
                    //         project['total'] = ''
                    //     } else if (fieldName === 'total' || fieldName === 'total_price_with_tax') { // 数量金额
                    //         project['price_with_tax'] = ''
                    //     }
                    // }else{
                    //     if (fieldName === 'total' || fieldName === 'price_with_tax') { //  数量单价
                    //         project['total_price_with_tax'] = ''
                    //     } else if (fieldName === 'total_price_with_tax') { //金额
                    //         project['total'] = ''
                    //     }
                    // }
                    if (fieldName === 'total' || fieldName === 'price_with_tax') { //  数量单价
                        project['total_price_with_tax'] = ''
                    } else if (fieldName === 'total_price_with_tax') { //金额
                        project['total'] = ''
                    }
                }
            }
        }

        invoiceProjects.splice(rowIndex, 1, project)
        this._updateInvoiceProjects(invoiceProjects)
    }

    _newDiscount(invoiceProject) {
        const { name, tax_rate, dis_type, discount, dis_total_price, dis_tax, dis_value } = invoiceProject
        return assignKey({
            _type: DISCOUNT_TYPE_KEY, // 折扣行标识，方便外边判断
            name,
            tax_rate, // 税率
            dis_type, // 折扣类型，1折扣率，2折扣金额
            discount, // 折扣金额（含税）
            dis_total_price, // 折扣金额（不含税）
            dis_value: dis_value || discount, // 折扣率或折扣金额，用户输入的值，计算时用到
            dis_tax, // 折扣税额
            total_price_with_tax: discount, // 折扣金额（含税），仅界面显示时用到
            tax_amount: dis_tax, // 折扣税额，仅界面显示用
        })
    }

    /**
     * 增加开票行数据的折扣行数据
     * 每个开票行数据的折扣行数据只允许存在一条折扣信息
     * 如果已存在，即替换原来的折扣信息
     * @param {Object} discountData    折扣信息 {type, discount}
     * @param {Number} rowIndex        开票数据的行索引，标识当前修改的是哪一条开票项目数据
     */
    @action addDiscount(discountData, rowIndex) {
        const invoiceProjects = this.invoiceProjects.slice(0)
        if (rowIndex >= invoiceProjects.length) {
            return false
        }

        const project = invoiceProjects[rowIndex]
        // 原开票数据中的商品名称、数量、单价不能为空
        if (!project.name || !project.total || !project.price_with_tax) {
            return false
        }
        // 折扣类型，1折扣率，2折扣金额
        project.dis_type = discountData.type
        // 折扣率或折扣金额，这里直接赋值，该值会在调用计算接口后，更新其dis_total_price
        project.dis_value = discountData.discount

        // 添加或更新折扣信息
        const insertIndex = rowIndex + 1
        const newDiscountData = this._newDiscount(project)
        const nextRecord = invoiceProjects[insertIndex]
        const deleteCount = nextRecord && this._isDiscountRecord(nextRecord) ? 1 : 0
        invoiceProjects.splice(insertIndex, deleteCount, newDiscountData)

        // 更新原数组数据
        this._updateInvoiceProjects(invoiceProjects)
    }

    /**
     * 计算开票行税额、金额、折扣等数据，计算逻辑分为3种情况：
     *
     * 1. 当3个输入框都为空时，不处理
     * 2. 当任意两个有值，而另一个为空时，计算另一个的值
     * 3. 当都不为空时，输入数量或单价时，计算金额；当输入金额时，计算数量
     */
    _countInvoice = debounce(() => {
        const invoiceProjects = this.invoiceProjects
        const records = []
        let i = 0
        let length = invoiceProjects.length

        for (; i < length; i++) {
            const project = invoiceProjects[i]
            const fields = ['total', 'price_with_tax', 'total_price_with_tax']
            const emptyLength = fields.filter(field => this._isEmptyField(project, field)).length
            // 商品名称或数量为空，或者数量、单价、金额三者任意二者为空时，直接忽略，不做计算处理
            if (this._isDiscountRecord(project) || !util.Trim(project.name) || emptyLength > 1) {
                continue
            }

            // 组装计算接口需要的数据
            const record = {
                list_id: i, // 当前行索引，方便在请求计算接口回来后做数据更新
                total_price_with_tax: project.total_price_with_tax, // 金额
                price_with_tax: project.price_with_tax, // 单价
                tax_rate: project.tax_rate, // 税率
                total: project.total, // 数量
            }

            // 针对折扣数据的处理
            if (this._isDiscountProject(project)) {
                record.dis_type = project.dis_type
                record.dis_value = Math.abs(project.dis_value || 0)
            }

            records.push(record)
        }

        // 如果存在需要计算的开票项目数据，则进行计算，否则就清零汇总信息
        if (records.length) {
            return this._requestCountInvoice(records)
        }

        // 清零汇总信息
        this._updateInvoiceData('invoice_info', {
            ...this.invoiceInfo,
            ...defaultInvoiceCountInfo
        })
    }, 1000)

    _requestCountInvoice = params => {
        return request({
            url: apis.countInvoice,
            method: 'post',
            data: { detail: JSON.stringify(params) },
            onLoadStart() {}
        }).then(res => {
            const { status, detail } = res || {}
            // 计算结果状态，0成功，1失败
            if (+status !== 0) {
                // TODO: 提示错误？
                return false
            }

            // 更新计算后的开票项目数据
            const invoiceProjects = this.invoiceProjects.slice(0)

            if (detail && detail.length) {
                detail.forEach(item => {
                    const invoiceProjectIndex = item.list_id
                    const project = invoiceProjects[invoiceProjectIndex]
                    // 更新开票项目的数量、单价、金额、税额以及折扣数据（其它与计算无关，不做变动）
                    project.price = item.price
                    project.total = item.total
                    project.price_with_tax = item.price_with_tax
                    project.total_price = item.total_price
                    project.total_price_with_tax = item.total_price_with_tax
                    project.tax_amount = item.tax

                    // 接口总是返回折扣数据，这里需要先判断原数据是否有折扣数据才更新折扣数据
                    if (this._isDiscountProject(project)) {
                        // 折扣类型，计算后，把折扣类型写死为折扣金额
                        project.dis_type = 2
                        // 折扣率或折扣金额，计算用，上面把折扣类型写死为2后，这里需要重它的值，避免在下次计算时有误
                        project.dis_value = item.discount
                        // 折扣金额（含税）
                        project.discount = item.discount
                        // 折扣金额(不含税)
                        project.dis_total_price = item.dis_total_price
                        // 折扣税额
                        project.dis_tax = item.dis_tax

                        // 更新其对应的折扣行数据
                        if (this._isDiscountRecord(invoiceProjects[invoiceProjectIndex + 1])) {
                            const newDiscountData = this._newDiscount(project)
                            invoiceProjects.splice(invoiceProjectIndex + 1, 1, newDiscountData)
                        }
                    }
                })
            }

            // 更新计算后的汇总数据
            const invoiceInfo = {
                ...this.invoiceInfo,
                total_tax: res.total_tax, // 总税额
                total_price: res.total_price, // 总金额（不含税）
                total_price_with_tax: res.total_price_with_tax, // 价税合计金额
                total_rmb: res.total_rmb, // 价税合计金额（大写）
            }

            this.invoiceData = {
                ...this.invoiceData,
                goods_info: invoiceProjects,
                invoice_info: invoiceInfo
            }
        })
    }
}

export default new Store()
